import { useCallback } from "react";
import { Box, Typography, Tooltip, IconButton, LinearProgress, alpha, useTheme } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DescriptionIcon from "@mui/icons-material/Description";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ImageIcon from "@mui/icons-material/Image";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useI18n } from "../../../../hooks/useI18n";

const IMAGE_EXTENSIONS = ["png", "jpeg", "jpg", "tiff"];
interface FileAttachmentProps {
  fileName: string;
  fileId: string;
  onDelete?: (fileId: string) => void;
  onDownload?: (fileId: string) => void;
  onPreview?: (fileId: string) => void;
  loading?: boolean;
}

export function FileAttachment({
  fileName,
  fileId,
  onDelete,
  onDownload,
  onPreview,
  loading = false,
}: FileAttachmentProps) {
  const theme = useTheme();
  const { t } = useI18n();

  const getFileIcon = useCallback(
    (name: string) => {
      const extension = name.split(".").pop()?.toLowerCase() || "";

      if (extension === "pdf") {
        return <DescriptionIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />;
      } else if (IMAGE_EXTENSIONS.includes(extension)) {
        return <ImageIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />;
      } else {
        return <InsertDriveFileIcon fontSize="small" sx={{ color: theme.palette.grey[600] }} />;
      }
    },
    [theme],
  );

  return (
    <Box
      sx={{
        display: "flex",
        p: 1.5,
        position: "relative",
        borderBottom: `1px solid ${theme.palette.divider}`,
        "&:hover": {
          backgroundColor: alpha(theme.palette.primary.light, 0.05),
        },
        transition: theme.transitions.create(["background-color"]),
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          overflow: "hidden",
          flex: 1,
        }}
      >
        <Box sx={{ mr: 1.5, display: "flex", alignItems: "center" }}>
          {getFileIcon(fileName)}
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "start",
            overflow: "hidden",
            flex: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
            <Tooltip title={fileName}>
              <Typography
                variant="body2"
                sx={{
                  textAlign: "left",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  width: "100%",
                  fontWeight: 500,
                }}
              >
                {fileName}
              </Typography>
            </Tooltip>
          </Box>
        </Box>

        <Box sx={{ display: "flex" }}>
          {onPreview && (
            <Tooltip title={t("common.cta.preview")}>
              <Box sx={{ visibility: loading ? 'hidden' : 'visible' }}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onPreview(fileId);
                  }}
                  disabled={loading}
                  sx={{
                    color: theme.palette.text.secondary,
                    mr: 0.5,
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.primary.light, 0.15),
                      color: theme.palette.primary.main,
                    },
                  }}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Box>
            </Tooltip>
          )}

          {onDownload && (
            <Tooltip title={t("common.cta.download")}>
              <Box sx={{ visibility: loading ? 'hidden' : 'visible' }}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDownload(fileId);
                  }}
                  disabled={loading}
                  sx={{
                    color: theme.palette.text.secondary,
                    mr: 0.5,
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.primary.light, 0.15),
                      color: theme.palette.primary.main,
                    },
                  }}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Box>
            </Tooltip>
          )}
          
          <Tooltip title={t("common.cta.delete")}>
            <Box sx={{ visibility: loading ? 'hidden' : 'visible' }}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete?.(fileId);
                }}
                disabled={loading}
                sx={{
                  color: theme.palette.text.secondary,
                  "&:hover": {
                    opacity: 1,
                    backgroundColor: alpha(theme.palette.error.light, 0.15),
                    color: theme.palette.error.main,
                  },
                }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          </Tooltip>
        </Box>
      </Box>

      {loading && (
        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}>
          <LinearProgress color="primary" sx={{ height: 2 }} />
        </Box>
      )}
    </Box>
  );
}
